import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import axiosClient from "../../axios";
import ClipLoader from "react-spinners/ClipLoader";

const Questions = () => {
  const [questions, setQuestions] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [categories, setCategories] = useState([]);
  const [flatOptions, setFlatOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const flattenCategories = (nodes, level = 0) => {
    return nodes.flatMap((node) => {
      const indent = "\u00A0\u00A0".repeat(level);
      const label = `${indent}${node.name}`;
      const option = {
        label,
        value: node.id,
        disabled: node.taxonomy !== "quiz",
      };

      const children = node.children
        ? flattenCategories(node.children, level + 1)
        : [];

      return [option, ...children];
    });
  };

  const fetchQuestions = async () => {
    if (!selectedCategory) {
      setQuestions([]);
      setTotal(0);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const params = {
        page: currentPage,
        perPage,
        category: selectedCategory,
      };

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/questions`,
        { params }
      );
      setQuestions(response.data.data);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching questions:", error);
      setError("Failed to load questions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/getTermsExcludingTaxonomy`,
        {
          params: {
            taxonomy: "video",
          },
        }
      );
      setCategories(response.data);
      const flat = flattenCategories(response.data);
      setFlatOptions(flat);
    } catch (err) {
      console.error("Error fetching categories:", err);
      setError("Failed to load categories. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchQuestions();
  }, [selectedCategory, currentPage, perPage]);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this question?")) {
      try {
        await axiosClient.delete(`/api/questions/${id}`);
        fetchQuestions();
      } catch (error) {
        console.error("Error deleting question:", error);
        setError("Failed to delete question. Please try again.");
      }
    }
  };

  return (
    <div className="container">
      <div className="d-flex align-items-center justify-content-start mb-4">
        <h2 className="mb-0 me-3">Questions</h2>
        <Link
          to="/xl-admin/add-question"
          className="btn btn-inchworm btn-sm"
          role="button"
          style={{
            padding: "0.25rem 0.4rem",
            fontSize: "0.8rem",
            lineHeight: "1",
          }}
        >
          Add New
        </Link>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="row mb-4">
        <div className="col-md-6 mb-2">
          <select
            className="form-select"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">Select a Category</option>
            {flatOptions.map((opt) => (
              <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3 mb-2">
          <select
            className="form-select"
            value={perPage}
            onChange={(e) => setPerPage(Number(e.target.value))}
          >
            <option value="10">10 per page</option>
            <option value="25">25 per page</option>
            <option value="50">50 per page</option>
            <option value="100">100 per page</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div
          className="d-flex flex-column justify-content-center align-items-center"
          style={{ height: "200px" }}
        >
          <ClipLoader
            color="#5cd400"
            loading={true}
            size={50}
            aria-label="Loading Spinner"
            data-testid="loader"
          />
          <div className="mt-3 text-muted fs-5">Loading, please wait...</div>
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-bordered table-striped">
              <thead className="table-success">
                <tr>
                  <th>ID</th>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {questions.length > 0 ? (
                  questions.map((question) => (
                    <tr key={question.id}>
                      <td>{question.id}</td>
                      <td>{question.question_text}</td>
                      <td>{question.category}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <Link
                            to={`/xl-admin/edit-question?id=${question.id}`}
                            className="btn btn-warning btn-sm"
                          >
                            Edit
                          </Link>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDelete(question.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center">
                      {selectedCategory
                        ? "No questions found for this category"
                        : "Please select a category"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {total > 0 && (
            <div className="d-flex justify-content-center mt-4">
              <nav>
                <ul className="pagination">
                  {Array.from(
                    { length: Math.ceil(total / perPage) },
                    (_, i) => (
                      <li
                        key={i}
                        className={`page-item ${
                          i + 1 === currentPage ? "active" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(i + 1)}
                        >
                          {i + 1}
                        </button>
                      </li>
                    )
                  )}
                </ul>
              </nav>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Questions;
